import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import { SUPPORTED_LANGUAGES } from '../i18n';
import { getStoredTempUnit, setStoredTempUnit } from '../utils/temperatureUtils';

const APP_VERSION = '1.3.2';

interface LayoutProps {
  children: React.ReactNode;
  user?: { email: string; full_name?: string | null; is_admin?: boolean; is_admin_grower?: boolean; language?: string };
}

const Layout: React.FC<LayoutProps> = ({ children, user: userProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [fetchedUser, setFetchedUser] = useState<LayoutProps['user'] | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [tempUnit, setTempUnit] = useState<string>(getStoredTempUnit());

  useEffect(() => {
    if (!userProp) {
      authService.getCurrentUser().then((u) => {
        setFetchedUser(u);
        if (u.language && u.language !== i18n.language) {
          i18n.changeLanguage(u.language);
        }
        if ((u as any).temperature_unit) {
          setTempUnit((u as any).temperature_unit);
          setStoredTempUnit((u as any).temperature_unit);
        }
      }).catch((err) => {
        console.error('Failed to fetch current user:', err);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProp]);

  useEffect(() => {
    if (!authService.isAuthenticated()) return;

    const sendHeartbeat = () => {
      authService.heartbeat().catch((err) => {
        console.error('Failed to update login presence:', err);
      });
    };
    sendHeartbeat();
    const heartbeatInterval = window.setInterval(sendHeartbeat, 60_000);
    return () => window.clearInterval(heartbeatInterval);
  }, []);

  // Apply user's language when userProp changes
  useEffect(() => {
    if (userProp?.language && userProp.language !== i18n.language) {
      i18n.changeLanguage(userProp.language);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProp?.language]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const user = userProp || fetchedUser;

  const handleLogout = () => {
    authService.logout();
    navigate('/login');
  };

  const handleLanguageChange = async (lang: string) => {
    setLangMenuOpen(false);
    await i18n.changeLanguage(lang);
    try {
      await authService.updateProfile({ language: lang });
    } catch (err) {
      console.error('Failed to save language preference:', err);
    }
  };

  const handleTempUnitChange = async (unit: string) => {
    setTempUnit(unit);
    setStoredTempUnit(unit);
    try {
      await authService.updateProfile({ temperature_unit: unit });
    } catch (err) {
      console.error('Failed to save temperature unit preference:', err);
    }
    // Reload the page so all charts refresh with new unit
    window.location.reload();
  };

  const navItems = [
    { path: '/dashboard', label: t('nav.dashboard'), adminOnly: false, adminGrowerAllowed: false },
    { path: '/growers', label: t('nav.growers'), adminOnly: true, adminGrowerAllowed: true },
    { path: '/sensors', label: t('nav.sensors'), adminOnly: false, adminGrowerAllowed: false },
    { path: '/thresholds', label: t('nav.thresholds'), adminOnly: false, adminGrowerAllowed: false },
    { path: '/users', label: t('nav.users'), adminOnly: true, adminGrowerAllowed: true },
    { path: '/settings', label: t('nav.settings'), adminOnly: true, adminGrowerAllowed: false },
    { path: '/gateways', label: t('nav.gateways'), adminOnly: true, adminGrowerAllowed: false },
  ];

  const isActive = (path: string) => location.pathname === path;

  // Filter nav items based on user role
  const visibleNavItems = navItems.filter(
    (item) => !item.adminOnly || user?.is_admin || (item.adminGrowerAllowed && user?.is_admin_grower)
  );

  const currentLang = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const appEnv = process.env.REACT_APP_ENV;

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Header */}
      <nav className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-16 items-center justify-between gap-2 py-2">
            <div className="flex min-w-0 flex-1 items-center gap-3 sm:gap-8">
              <div className="flex min-w-0 items-center space-x-2">
                <h1 className="max-w-[11rem] sm:max-w-none text-base sm:text-xl font-bold text-gray-900 truncate">
                  {t('nav.appTitle')}
                </h1>
                <span
                  className={`hidden sm:inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-medium ${
                    appEnv === 'staging'
                      ? 'bg-yellow-100 text-yellow-800'
                      : appEnv === 'production'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                  title={appEnv ? `Environment: ${appEnv}` : 'Local development'}
                >
                  v{APP_VERSION}{appEnv === 'staging' ? ' · staging' : appEnv === 'production' ? ' · prod' : ''}
                </span>
              </div>
              <div className="hidden md:flex space-x-4">
                {visibleNavItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </div>
            </div>
            <div className="flex shrink-0 items-center space-x-2 sm:space-x-4">
              {user && (
                <div className="hidden sm:flex items-center space-x-2">
                  <span className="text-sm text-gray-700">
                    {user.full_name || user.email}
                  </span>
                  {user.is_admin && (
                    <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
                      {t('common.admin')}
                    </span>
                  )}
                  {!user.is_admin && user.is_admin_grower && (
                    <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                      Grower Admin
                    </span>
                  )}
                </div>
              )}

              {/* Temperature Unit Toggle */}
              <div className="hidden sm:flex items-center">
                <button
                  onClick={() => handleTempUnitChange(tempUnit === 'C' ? 'F' : 'C')}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
                  title="Toggle temperature unit"
                >
                  <span className={tempUnit === 'C' ? 'font-semibold text-primary-700' : 'text-gray-400'}>°C</span>
                  <span className="mx-1 text-gray-400">/</span>
                  <span className={tempUnit === 'F' ? 'font-semibold text-primary-700' : 'text-gray-400'}>°F</span>
                </button>
              </div>

              {/* Language Selector */}
              <div className="relative hidden sm:block">
                <button
                  onClick={() => setLangMenuOpen(!langMenuOpen)}
                  className="flex items-center space-x-1 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
                  title={t('nav.language')}
                >
                  <span>{currentLang.code.toUpperCase()}</span>
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {langMenuOpen && (
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200">
                    {SUPPORTED_LANGUAGES.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => handleLanguageChange(lang.code)}
                        className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                          i18n.language === lang.code ? 'font-semibold text-primary-700' : 'text-gray-700'
                        }`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button
                onClick={handleLogout}
                className="hidden sm:inline-flex px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
              >
                {t('nav.logout')}
              </button>
              {/* Mobile menu button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:bg-gray-100 focus:outline-none"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {visibleNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-3 py-2 rounded-md text-base font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
            {/* Mobile language selector */}
            <div className="border-t border-gray-200 px-4 py-3">
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">{t('nav.language')}</p>
              <div className="flex flex-wrap gap-2">
                {SUPPORTED_LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code)}
                    className={`px-3 py-1 text-sm rounded-full border ${
                      i18n.language === lang.code
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {lang.code.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            {/* Mobile temperature unit selector */}
            <div className="border-t border-gray-200 px-4 py-3">
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">Temperature Unit</p>
              <div className="flex gap-2">
                {['C', 'F'].map((unit) => (
                  <button
                    key={unit}
                    onClick={() => handleTempUnitChange(unit)}
                    className={`px-3 py-1 text-sm rounded-full border ${
                      tempUnit === unit
                        ? 'bg-primary-600 text-white border-primary-600'
                        : 'text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    °{unit}
                  </button>
                ))}
              </div>
            </div>
            <div className="border-t border-gray-200 px-4 py-3">
              {user && (
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 mb-3">
                    <span className="text-sm font-medium text-gray-700 truncate">
                      {user.full_name || user.email}
                    </span>
                    {user.is_admin && (
                      <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-800 rounded-full">
                        {t('common.admin')}
                      </span>
                    )}
                    {!user.is_admin && user.is_admin_grower && (
                      <span className="px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">
                        Grower Admin
                      </span>
                    )}
                  </div>
                  <Link
                    to="/profile"
                    className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md"
                  >
                    {t('nav.profile')}
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full px-3 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors"
                  >
                    {t('nav.logout')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-4 sm:py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0 overflow-x-auto">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
